import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaMapMarkerAlt, FaCalendarAlt, FaClock, FaStar, FaCheckCircle, FaTimesCircle, FaHotel, FaMountain } from "react-icons/fa";
import "../css/styles.css";

const KilimanjaroTsavo = () => {
  return (
    <div className="safari-detail-page">
      {/* ===== HERO ===== */}
      <section className="safari-detail-hero">
        <div className="safari-detail-hero-bg">
          <img
            src="https://res.cloudinary.com/dn39bkkiq/image/upload/q_auto,f_auto,w_1400/v1776348587/6_Day_Kilimanjaro_Tsavo_Safari_kwzzin.jpg"
            alt="Kilimanjaro and Tsavo Safari"
          />
          <div className="safari-detail-hero-overlay" />
        </div>
        <div className="safari-detail-hero-content">
          <span className="safari-detail-eyebrow">Kenya Safari</span>
          <h1>6 Day Kilimanjaro &amp; Tsavo Safari</h1>
          <p className="safari-detail-hero-sub">
            From the snow-capped peak of Mount Kilimanjaro to the rugged red landscapes of Tsavo — an unforgettable Kenyan adventure.
          </p>
          <div className="safari-detail-hero-buttons">
            <Link to="/contact" className="hero-cta primary">
              Plan Your Safari <FaArrowRight className="btn-icon" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== OVERVIEW ===== */}
      <section className="safari-detail-overview">
        <div className="safari-detail-section-header">
          <span className="section-tag">Safari Overview</span>
          <h2>A Journey Through Kenya's Wild Heart</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-text">
          <p>
            This safari takes you through some of Kenya's most beautiful wilderness areas. From the wide plains of Amboseli beneath the snow-capped peak of Mount Kilimanjaro to the rugged landscapes of Tsavo and the quiet charm of Taita Hills, each day reveals a different face of Africa.
          </p>
          <p>
            In Amboseli, elephants move slowly across the dusty plains as giraffes and zebras graze under the shadow of Africa's tallest mountain. In Tsavo, the land turns red and wild, home to lions, buffalo, and herds of elephants known for their reddish dust-covered skin.
          </p>
          <p>
            The journey ends at the peaceful Taita Hills Wildlife Sanctuary, where animals gather at waterholes just below your lodge. It is a place where evenings are calm, the air is cool, and the sounds of the wild fill the night.
          </p>
          <p>
            This safari is perfect for travelers who want to experience Kenya's wildlife, landscapes, and quiet moments in nature in a short but unforgettable adventure.
          </p>
        </div>
      </section>

      {/* ===== HIGHLIGHTS ===== */}
      <section className="safari-detail-highlights">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Highlights</span>
          <h2>Safari Highlights</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-highlights-list">
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Stunning views of Mount Kilimanjaro in Amboseli</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Large herds of elephants roaming the open plains</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Scenic drives through the wild landscapes of Tsavo</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Unique wildlife viewing at Taita Hills Wildlife Sanctuary</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Overnight stay at the famous Salt Lick Safari Lodge, overlooking a busy waterhole</p>
          </div>
        </div>
      </section>

      {/* ===== AT A GLANCE ===== */}
      <section className="safari-detail-glance">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Quick Info</span>
          <h2>Safari at a Glance</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-glance-grid">
          <div className="safari-detail-glance-card">
            <FaClock className="safari-detail-glance-icon" />
            <h4>Duration</h4>
            <p>6 Days / 5 Nights</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaMapMarkerAlt className="safari-detail-glance-icon" />
            <h4>Start / End</h4>
            <p>Nairobi</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaMapMarkerAlt className="safari-detail-glance-icon" />
            <h4>Destinations</h4>
            <p>Amboseli &ndash; Tsavo West &ndash; Taita Hills</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaCalendarAlt className="safari-detail-glance-icon" />
            <h4>Best Time to Travel</h4>
            <p>All year round</p>
          </div>
        </div>
      </section>

      {/* ===== ITINERARY ===== */}
      <section className="safari-detail-itinerary">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Day by Day</span>
          <h2>Day-by-Day Itinerary</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-days">
          {/* Day 1 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">1</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 1 — Arrival in Nairobi</h3>
              <p>
                Welcome to Kenya. Upon arrival at Jomo Kenyatta International Airport, a Nyatwende representative will meet you and transfer you to your hotel in Nairobi.
              </p>
              <p>
                Depending on your arrival time, you may simply relax after your journey or explore some of Nairobi's well-known attractions. You may visit the giraffes at the Giraffe Centre or the rescued baby elephants at the David Sheldrick Wildlife Trust.
              </p>
              <p className="safari-detail-day-note">Overnight in Nairobi.</p>
            </div>
          </div>

          {/* Day 2 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">2</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 2 — Nairobi to Amboseli National Park</h3>
              <p>
                After breakfast, the journey begins as you travel south towards Amboseli National Park. The road slowly leaves the busy city behind and opens into wide plains with scattered acacia trees.
              </p>
              <p>
                Amboseli is famous for its elephants and the breathtaking sight of Mount Kilimanjaro rising above the clouds.
              </p>
              <p>
                You will arrive at your lodge in time for lunch and some rest before heading out for your first afternoon game drive. As the sun begins to soften, wildlife becomes more active. Elephants wander across the plains while zebras and antelopes graze quietly.
              </p>
              <p className="safari-detail-day-note">Overnight in Amboseli.</p>
            </div>
          </div>

          {/* Day 3 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">3</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 3 — A Full Day in Amboseli</h3>
              <p>
                Today is dedicated to exploring Amboseli. You will head out early in the morning when the park is fresh and quiet. This is one of the best times to see wildlife as animals begin their day.
              </p>
              <p>
                Large elephant families move across the land, often walking slowly beneath the towering presence of Mount Kilimanjaro. If the skies are clear, the mountain reveals its snow-covered peak, creating one of Africa's most iconic views.
              </p>
              <p>
                After lunch and some time to relax at the lodge, another game drive takes place in the afternoon, offering more chances to see lions, buffalo, giraffes, and many species of birds.
              </p>
              <p className="safari-detail-day-note">Overnight in Amboseli.</p>
            </div>
          </div>

          {/* Day 4 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">4</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 4 — Amboseli to Tsavo West</h3>
              <p>
                After breakfast, you continue your journey towards Tsavo West National Park. The scenery changes as the land becomes more rugged and volcanic.
              </p>
              <p>
                Tsavo is one of Kenya's largest parks, known for its vast landscapes and untamed beauty.
              </p>
              <p>
                After arriving and enjoying lunch at your lodge, an afternoon game drive begins. Here you may see elephants, lions, leopards, and the famous red-dust elephants that Tsavo is known for.
              </p>
              <p>
                If time allows, you may also visit the beautiful Mzima Springs, where clear water flows through pools filled with fish and hippos.
              </p>
              <p className="safari-detail-day-note">Overnight in Tsavo West.</p>
            </div>
          </div>

          {/* Day 5 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">5</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 5 — Tsavo West to Taita Hills Wildlife Sanctuary</h3>
              <p>
                After breakfast, you leave Tsavo and drive towards Taita Hills Wildlife Sanctuary, a smaller but very special reserve.
              </p>
              <p>
                You will arrive in time for lunch at the unique Salt Lick Safari Lodge. The lodge is built on raised platforms overlooking a busy waterhole where animals often gather throughout the day and night.
              </p>
              <p>
                In the afternoon, enjoy a game drive through the sanctuary. Despite its small size, the reserve is rich with wildlife including elephants, lions, buffalo, and many antelope species.
              </p>
              <p>
                As evening falls, you can sit quietly on the lodge terrace and watch animals come to drink at the waterhole below.
              </p>
              <p className="safari-detail-day-note">Overnight at Salt Lick Safari Lodge.</p>
            </div>
          </div>

          {/* Day 6 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">6</div>
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 6 — Return to Nairobi</h3>
              <p>
                After breakfast, it is time to begin the journey back to Nairobi. The drive takes you through changing landscapes and small rural towns, offering one last glimpse of Kenya's countryside.
              </p>
              <p>
                Arrival in Nairobi will be in the afternoon, where you will be dropped off at the airport or your hotel depending on your travel plans.
              </p>
              <p className="safari-detail-day-note">
                Your safari ends here, but the memories of Kenya's wildlife and landscapes will stay with you long after the journey.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ACCOMMODATION ===== */}
      <section className="safari-detail-accommodation">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Where You'll Stay</span>
          <h2>Accommodation Examples</h2>
          <div className="safari-detail-line" />
        </div>

        {/* Amboseli */}
        <h3 className="safari-detail-accom-region-title">Amboseli National Park</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier budget">Budget</div>
            <ul>
              <li><FaHotel /> Sentrim Amboseli Lodge</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Midrange</div>
            <ul>
              <li><FaHotel /> Amboseli Serena Safari Lodge</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Luxury</div>
            <ul>
              <li><FaHotel /> Ol Tukai Lodge</li>
            </ul>
          </div>
        </div>

        {/* Tsavo West */}
        <h3 className="safari-detail-accom-region-title">Tsavo West National Park</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier budget">Budget</div>
            <ul>
              <li><FaHotel /> Ngulia Safari Lodge</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Midrange</div>
            <ul>
              <li><FaHotel /> Severin Safari Camp</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Luxury</div>
            <ul>
              <li><FaHotel /> Kilaguni Serena Safari Lodge</li>
            </ul>
          </div>
        </div>

        {/* Taita Hills */}
        <h3 className="safari-detail-accom-region-title">Taita Hills Wildlife Sanctuary</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Midrange</div>
            <ul>
              <li><FaHotel /> Salt Lick Safari Lodge</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Alternative</div>
            <ul>
              <li><FaHotel /> Taita Hills Safari Resort &amp; Spa</li>
            </ul>
          </div>
        </div>

        <p className="safari-detail-accom-note">
          Similar lodges of the same standard may be used depending on availability.
        </p>
      </section>

      {/* ===== INCLUDED / EXCLUDED ===== */}
      <section className="safari-detail-inclusions">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">What to Expect</span>
          <h2>What's Included</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-inclusions-grid">
          <div className="safari-detail-inclusions-card included">
            <h3><FaCheckCircle /> Included</h3>
            <ul>
              <li>Airport transfers</li>
              <li>Transport in a comfortable safari vehicle with a professional guide</li>
              <li>All park entrance fees</li>
              <li>Accommodation during the safari</li>
              <li>All meals as specified in the itinerary</li>
              <li>Bottled drinking water during game drives</li>
            </ul>
          </div>
          <div className="safari-detail-inclusions-card excluded">
            <h3><FaTimesCircle /> Not Included</h3>
            <ul>
              <li>International flights</li>
              <li>Travel insurance</li>
              <li>Visa fees</li>
              <li>Optional excursions</li>
              <li>Personal expenses</li>
              <li>Tips and gratuities</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="safari-detail-cta">
        <div className="safari-detail-cta-content">
          <h2>Customize This Safari</h2>
          <p>
            Every traveler is different. Safari duration, lodge preferences, and group size can all be adjusted to create the perfect journey. Tell us what you have in mind and we will design a safari that fits you perfectly.
          </p>
          <div className="safari-detail-cta-buttons">
            <Link to="/contact?safari=6 Day Kilimanjaro and Tsavo Safari" className="hero-cta primary">
              Plan Your Safari <FaArrowRight className="btn-icon" />
            </Link>
            <Link to="/tours" className="hero-cta secondary">
              View More Safaris
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default KilimanjaroTsavo;
